import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, Btn, Table } from "@/components/UI";
import { ArrowLeft, AlertTriangle, ShoppingCart } from "lucide-react";
import { OrderStatusBadge, StockStatusBadge } from "@/components/sales/OrderBadges";
import { OrderActions } from "@/components/sales/OrderActions";
import { useSalesCapabilities } from "@/lib/useSalesCapabilities";
import { SOURCE_LABEL, trDate } from "@/lib/sales";
import { fmtNum, fmtQty } from "@/lib/products";
import { apiError, typeOf } from "@/lib/customers";
import { MetaErpPanel } from "@/components/sales/MetaErpPanel";

function TelegramNotice({ so }) {
  if (so.source !== "TELEGRAM" || (!so.needs_customer_match && !(so.pending_lines || []).length)) return null;
  return (
    <Card className="p-3 mb-4 border-amber-300 bg-amber-50 text-xs text-amber-900" data-testid="so-telegram-notice">
      <div className="font-semibold">Telegram'dan gelen taslak — onaydan önce düzenleyin</div>
      {so.needs_customer_match && <div data-testid="so-needs-customer">Müşteri eşleşmesi gerekli{so.telegram?.username ? ` (Telegram: @${so.telegram.username})` : ""}</div>}
      {(so.pending_lines || []).map((p, i) => <div key={i} data-testid={`so-pending-${i}`}>Eşleşmeyen ürün: <b>{p.text}</b>{p.qty ? ` (${p.qty} adet)` : ""}</div>)}
      {so.telegram?.text && <div className="italic mt-1">“{so.telegram.text}”</div>}
    </Card>
  );
}

const KV = ({ l, v, testid }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div><div className="text-sm" data-testid={testid}>{v || "—"}</div></div>
);

function StockPanel({ so }) {
  const nav = useNavigate();
  const items = so.stock_check?.items || [];
  const short = items.filter((i) => i.shortage > 0);
  return (
    <Card className="p-4" data-testid="so-stock-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">Stok Kontrolü</div>
        <StockStatusBadge status={so.status === "cancelled" ? null : so.stock_check?.status} testid="so-detail-stock-status" />
      </div>
      {items.map((i) => (
        <div key={i.product_id} className="text-xs py-1 border-b border-slate-100" data-testid={`so-stock-item-${i.product_id}`}>
          <div className="font-medium">{i.product_name}</div>
          <div className="text-slate-600">İhtiyaç {fmtQty(i.need)} · Kullanılabilir {fmtQty(i.available)} · Rezerve (bu sipariş) {fmtQty(so.reserved?.[i.product_id] || 0)}</div>
          {i.shortage > 0 && <div className="text-rose-600 font-semibold flex items-center gap-1" data-testid={`so-shortage-${i.product_id}`}><AlertTriangle size={12} /> {fmtQty(i.shortage)} adet eksik</div>}
        </div>
      ))}
      {short.length > 0 && ["approved", "partial"].includes(so.status) && short.map((i) => (
        <Btn key={i.product_id} size="sm" variant="secondary" className="mt-2 mr-1" data-testid={`so-create-po-${i.product_id}`}
          onClick={() => nav(`/purchasing?product_id=${i.product_id}&qty=${i.shortage}&so_id=${so.id}&so_no=${encodeURIComponent(so.so_no)}`)}>
          <ShoppingCart size={12} /> Satın Alma Oluştur{short.length > 1 ? ` — ${i.product_name}` : ""} ({fmtQty(i.shortage)})
        </Btn>
      ))}
      {so.status === "draft" && <div className="text-[11px] text-slate-500 mt-2">Taslak sipariş stok rezerve etmez; onayda rezervasyon oluşur.</div>}
    </Card>
  );
}

export default function SalesOrderDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const caps = useSalesCapabilities();
  const [so, setSo] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`/sales-orders/${id}`).then(setSo).catch((e) => setErr(apiError(e, "Sipariş bulunamadı")));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="p-6 text-rose-600" data-testid="so-detail-error">{err}</div>;
  if (!so) return <div className="p-6 text-slate-500">Yükleniyor…</div>;
  const t = so.totals;
  return (
    <div data-testid="so-detail-page">
      <PageHeader title={<span data-testid="so-detail-no">{so.so_no}</span>} description={so.customer_name || "Müşteri eşleşmesi gerekli"}
        actions={<Btn variant="secondary" onClick={() => nav("/sales-orders")} data-testid="so-detail-back"><ArrowLeft size={14} /> Liste</Btn>} />
      <TelegramNotice so={so} />
      <div className="mb-4"><OrderActions so={so} caps={caps} showDetail={false} onChanged={(r) => (r?.trashed ? nav("/sales-orders") : load())} /></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
          <KV l="Durum" v={<OrderStatusBadge status={so.status} testid="so-detail-status" />} />
          <KV l="Kaynak" v={SOURCE_LABEL[so.source]} />
          <KV l="Müşteri Ref. No" v={so.customer_ref} testid="so-detail-ref" />
          <KV l="Fiyat Grubu" v={typeOf(so.customer_snapshot?.price_group)?.label} />
          <KV l="Sipariş Tarihi" v={trDate(so.order_date)} />
          <KV l="Teslim Tarihi" v={trDate(so.delivery_date)} />
          <KV l="Vade" v={`${so.due_days} gün · ${trDate(so.due_date)}`} testid="so-detail-due" />
          <KV l="İskonto PDF'te" v={so.show_discount ? "Gösterilir" : "Gizli"} />
          <div className="col-span-2 md:col-span-4"><KV l="Adres" v={so.customer_snapshot?.billing_address} /></div>
        </Card>
        <StockPanel so={so} />
      </div>
      <Card className="p-4 mt-4">
        <Table testid="so-detail-lines" rows={so.lines.map((l, i) => ({ ...l, id: i }))} columns={[
          { label: "Ürün", render: (l) => <div><div className="font-medium">{l.product_name}</div><div className="text-[11px] text-slate-500 font-mono">{l.product_code}</div></div> },
          { label: "Adet", render: (l) => <span data-testid={`so-detail-qty-${l.id}`}>{fmtQty(l.qty)}</span> },
          { label: "MF", render: (l) => l.mf_ratio || "—" },
          { label: "Bedelsiz", render: (l) => <span data-testid={`so-detail-free-${l.id}`}>{fmtQty(l.free_qty)}</span> },
          { label: "Fiziksel", render: (l) => fmtQty(l.physical_qty) },
          { label: "Liste Fiyat", render: (l) => fmtNum(l.list_price) },
          { label: "Birim Fiyat", render: (l) => <span className={l.price_overridden ? "text-amber-700 font-semibold" : ""} data-testid={`so-detail-price-${l.id}`}>{fmtNum(l.unit_price)}</span> },
          { label: "İsk. %", render: (l) => l.discount_pct },
          { label: "KDV %", render: (l) => l.vat_pct },
          { label: "Satır Net", render: (l) => <b>{fmtNum(l.line_net)}</b> },
        ]} />
        <div className="flex justify-end mt-3">
          <div className="text-sm w-64 space-y-0.5" data-testid="so-detail-totals">
            <div className="flex justify-between"><span>Brüt</span><span>{fmtNum(t.gross)}</span></div>
            <div className="flex justify-between"><span>İskonto</span><span>-{fmtNum(t.discount)}</span></div>
            <div className="flex justify-between"><span>Ara Toplam</span><span>{fmtNum(t.net)}</span></div>
            <div className="flex justify-between"><span>KDV</span><span>{fmtNum(t.vat)}</span></div>
            <div className="flex justify-between font-bold border-t pt-1"><span>Genel Toplam</span><span data-testid="so-detail-grand">{fmtNum(t.grand_total)} TRY</span></div>
          </div>
        </div>
      </Card>
      <MetaErpPanel so={so} canSend={caps.approve} />
    </div>
  );
}
